import React, { useMemo } from 'react';
import useTbsDemoState from '../components/tbs-demo/useTbsDemoState';
import Scout from '../components/tbs-demo/Scout';
import OverviewView from '../components/tbs-demo/OverviewView';
import InboxView from '../components/tbs-demo/InboxView';
import ComingSoonView from '../components/tbs-demo/ComingSoonView';
import WarehouseView from '../components/tbs-demo/WarehouseView';
import { SUP, AG } from '../components/tbs-demo/data';

const FONT_HREF = 'https://fonts.googleapis.com/css2?family=Geist:wght@400;500;600&family=Geist+Mono:wght@400;500&family=Instrument+Serif&display=swap';

// Native React port of the original `TBS Agentic Demo.dc.html` — the
// scripted, fully-interactive Total Buah Segar agent demo. Overview and
// Inbox are fully ported and Warehouse runs on live data; the rest of the nav (Orders/Dispatch/
// Procurement/Stores/Agents/Portal) is a clearly-labeled placeholder for
// now, per the staged plan.
export default function TBSDashboard() {
  const { state: s, actions, threadRef, helpers } = useTbsDemoState();

  const shell = useMemo(() => {
    const human = s.convs.filter((x) => x.status === 'human');
    const drafts = s.pos.filter((p) => p.status === 'draft');
    const openEx = s.exc.filter((e) => e.status === 'open');
    const agentsLive = Object.keys(AG).filter((k) => !s.off[k]).length;

    const views = s.role === 'supplier'
      ? [['portal', 'Purchase orders', s.pos.filter((p) => p.sup === s.supId && p.status === 'sent').length]]
      : [['overview', 'Overview'], ['inbox', 'Inbox', human.length], ['orders', 'Orders'], ['warehouse', 'Warehouse'], ['dispatch', 'Dispatch'], ['stores', 'Stores'], ['procurement', 'Procurement', drafts.length + openEx.length], ['agents', 'Agents']];

    const nav = views.map(([k, l, b]) => ({
      key: k, label: l, badge: b || 0,
      bg: s.view === k ? '#14141A' : 'transparent', color: s.view === k ? '#F2F2F4' : '#9C9CA6',
      onClick: () => actions.setView(k),
    }));

    const roles = [['staff', 'Total Buah Segar'], ['supplier', 'Supplier']].map(([k, l]) => ({
      key: k, label: l, bg: s.role === k ? '#2F6BFF' : 'transparent', color: s.role === k ? '#fff' : '#9C9CA6',
      onClick: () => actions.patch({ role: k, view: k === 'supplier' ? 'portal' : 'procurement' }),
    }));

    const isSupplier = s.role === 'supplier';
    const userName = isSupplier ? SUP[s.supId].contact : 'Johan Sutheja';
    const userRole = isSupplier ? SUP[s.supId].name : 'Procurement · Total Buah Segar';
    const userInitials = (isSupplier ? SUP[s.supId].contact.replace(/^(Pak|Bu) /, '') : 'Johan Sutheja').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();

    const gs = actions.guide();
    const cur = gs.findIndex((g) => !g.done);
    const doneCount = gs.filter((g) => g.done).length;

    return {
      nav, roles, isSupplier,
      liveLabel: isSupplier ? 'Tara online' : `${agentsLive} agents live`,
      supOpts: Object.entries(SUP).map(([id, v]) => ({ id, name: v.name })),
      userName, userRole, userInitials, userColor: isSupplier ? '#FF8A5B' : '#2F6BFF',
      hasToast: !!s.toast, toastText: s.toast?.text, toastKey: s.toast?.agent || 'scout',
      guideSteps: gs.map((g, i) => ({
        n: i + 1, t: g.t, d: g.d, done: g.done, isCur: i === cur,
        numBg: g.done ? '#2F6BFF' : i === cur ? '#F2F2F4' : 'transparent',
        numColor: g.done || i === cur ? (g.done ? '#fff' : '#050506') : '#6A6A74',
        numBorder: g.done || i === cur ? 'transparent' : '#26262E',
        titleColor: g.done ? '#6A6A74' : i === cur ? '#F2F2F4' : '#9C9CA6',
        bg: i === cur ? '#0B1328' : 'transparent', border: i === cur ? '#1C2A55' : 'transparent',
        run: g.run, runLabel: s.busy ? 'Agent working…' : 'Run this step',
      })),
      guideProgress: `${doneCount}/${gs.length}`,
      guideBarW: (doneCount / gs.length) * 100 + '%',
      guideBubble: cur < 0 ? 'Demo complete — reset to run it again.' : `Step ${cur + 1}: ${gs[cur].t}`,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s, actions]);

  return (
    <div style={{ height: '100vh', minWidth: 1360, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative', background: '#050506', fontFamily: "'Geist',system-ui,sans-serif", color: '#F2F2F4', WebkitFontSmoothing: 'antialiased' }}>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link href={FONT_HREF} rel="stylesheet" />
      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes blink{0%,80%,100%{opacity:.25}40%{opacity:1}}
        @keyframes bob{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
        .tbs-user-meta{display:flex}
        /* Nav + role switch + name overflow the header below ~1600px; the avatar stays. */
        @media (max-width:1600px){.tbs-user-meta{display:none}}
      `}</style>

      <header style={{ height: 56, flex: 'none', display: 'flex', alignItems: 'center', gap: 24, padding: '0 20px', background: '#050506', borderBottom: '1px solid #1C1C22' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 24, height: 24, borderRadius: '50%', border: '1.5px solid #F2F2F4', display: 'grid', placeItems: 'center' }}><div style={{ width: 7, height: 7, borderRadius: '50%', background: '#F2F2F4' }} /></div>
          <span style={{ fontSize: 17, fontWeight: 500, letterSpacing: '-.01em' }}>grocera</span>
          <span style={{ font: "500 10px 'Geist Mono',monospace", letterSpacing: '.14em', color: '#9C9CA6', border: '1px solid #26262E', padding: '3px 8px', borderRadius: 20, whiteSpace: 'nowrap' }}>× TOTAL BUAH SEGAR</span>
        </div>
        <nav style={{ display: 'flex', gap: 2 }}>
          {shell.nav.map((n) => (
            <button key={n.key} onClick={n.onClick} style={{ border: 0, background: n.bg, color: n.color, font: "500 10.5px 'Geist Mono',monospace", letterSpacing: '.14em', textTransform: 'uppercase', padding: '8px 10px', borderRadius: 7, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap' }}>
              {n.label}
              {!!n.badge && <span style={{ minWidth: 17, height: 17, borderRadius: 9, background: '#2F6BFF', color: '#fff', fontSize: 10, letterSpacing: 0, display: 'grid', placeItems: 'center', padding: '0 5px' }}>{n.badge}</span>}
            </button>
          ))}
        </nav>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, font: "500 10px 'Geist Mono',monospace", letterSpacing: '.12em', color: '#9C9CA6', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#2F6BFF', boxShadow: '0 0 8px #2F6BFF' }} />{shell.liveLabel}
          </div>
          <div style={{ display: 'flex', gap: 3, background: '#0E0E12', border: '1px solid #1C1C22', padding: 3, borderRadius: 9 }}>
            {shell.roles.map((r) => (
              <button key={r.key} onClick={r.onClick} style={{ border: 0, background: r.bg, color: r.color, borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap' }}>{r.label}</button>
            ))}
          </div>
          {shell.isSupplier && (
            <select value={s.supId} onChange={(e) => actions.patch({ supId: e.target.value })} style={{ height: 30, border: '1px solid #26262E', borderRadius: 8, padding: '0 8px', fontSize: 12, background: '#0E0E12', color: '#F2F2F4' }}>
              {shell.supOpts.map((so) => <option key={so.id} value={so.id}>{so.name}</option>)}
            </select>
          )}
          <div className="tbs-user-meta" style={{ flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.25 }}>
            <span style={{ fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap' }}>{shell.userName}</span>
            <span style={{ fontSize: 11, color: '#6A6A74', whiteSpace: 'nowrap' }}>{shell.userRole}</span>
          </div>
          <div style={{ width: 30, height: 30, borderRadius: '50%', background: shell.userColor, color: '#fff', fontSize: 11.5, fontWeight: 600, display: 'grid', placeItems: 'center' }}>{shell.userInitials}</div>
        </div>
      </header>

      {shell.hasToast && (
        <div style={{ position: 'absolute', top: 68, right: 20, zIndex: 20, display: 'flex', alignItems: 'center', gap: 10, background: '#14141A', border: '1px solid #26262E', padding: '8px 14px 8px 8px', borderRadius: 12, boxShadow: '0 12px 32px rgba(0,0,0,.5)', fontSize: 13, maxWidth: 440 }}>
          <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#0A0A0D', border: '1px solid #26262E', display: 'grid', placeItems: 'center', flex: 'none', overflow: 'hidden' }}>
            <Scout agent={shell.toastKey} head style={{ width: 26, height: 26 }} />
          </div>
          <span>{shell.toastText}</span>
        </div>
      )}

      {s.view === 'overview' && <OverviewView state={s} actions={actions} helpers={helpers} />}
      {s.view === 'inbox' && <InboxView state={s} actions={actions} threadRef={threadRef} helpers={helpers} />}
      {s.view === 'warehouse' && <WarehouseView onEnter={() => actions.patch({ guideOpen: false })} />}
      {!['overview', 'inbox', 'warehouse'].includes(s.view) && <ComingSoonView view={s.view} />}

      <div style={{ position: 'absolute', left: 20, bottom: 18, zIndex: 30, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 10, pointerEvents: 'none' }}>
        {s.guideOpen && (
          <div style={{ pointerEvents: 'auto', width: 380, maxHeight: 'calc(100vh - 190px)', display: 'flex', flexDirection: 'column', background: 'rgba(10,10,13,.96)', backdropFilter: 'blur(10px)', border: '1px solid #26262E', borderRadius: 16, boxShadow: '0 20px 50px rgba(0,0,0,.6)', overflow: 'hidden' }}>
            <div style={{ padding: '14px 16px 12px', borderBottom: '1px solid #1C1C22', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ font: "500 10px 'Geist Mono',monospace", letterSpacing: '.18em', color: '#8AAEFF', flex: 1 }}>SCOUT · DEMO SCRIPT</span>
                <span style={{ font: "500 11px 'Geist Mono',monospace", color: '#9C9CA6' }}>{shell.guideProgress}</span>
                <button onClick={actions.reset} style={{ border: '1px solid #26262E', background: 'transparent', color: '#9C9CA6', font: "500 10px 'Geist Mono',monospace", letterSpacing: '.12em', padding: '4px 8px', borderRadius: 6, cursor: 'pointer' }}>RESET</button>
              </div>
              <div style={{ font: "400 22px/1.15 'Instrument Serif',serif" }}>Total Buah Segar, from chat to dock.</div>
              <div style={{ height: 3, borderRadius: 2, background: '#1C1C22', overflow: 'hidden' }}><div style={{ height: '100%', width: shell.guideBarW, background: '#2F6BFF', transition: 'width .4s' }} /></div>
            </div>
            <div style={{ overflow: 'auto', padding: 8 }}>
              {shell.guideSteps.map((g) => (
                <div key={g.n} style={{ display: 'grid', gridTemplateColumns: '24px minmax(0,1fr)', gap: 11, padding: 10, borderRadius: 10, background: g.bg, border: `1px solid ${g.border}` }}>
                  <div style={{ width: 22, height: 22, borderRadius: 6, background: g.numBg, color: g.numColor, border: `1px solid ${g.numBorder}`, font: "500 11px 'Geist Mono',monospace", display: 'grid', placeItems: 'center' }}>{g.n}</div>
                  <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: g.titleColor }}>{g.t}</div>
                    {g.isCur && (
                      <>
                        <div style={{ fontSize: 12, lineHeight: 1.5, color: '#9C9CA6' }}>{g.d}</div>
                        <div style={{ display: 'flex', gap: 6, marginTop: 3 }}>
                          <button onClick={g.run} style={{ height: 30, padding: '0 12px', borderRadius: 8, border: 0, background: '#2F6BFF', color: '#fff', fontSize: 12, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7 }}>▶ {g.runLabel}</button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div style={{ pointerEvents: 'auto', display: 'flex', alignItems: 'flex-end', gap: 8 }}>
          <button onClick={() => actions.patch((p) => ({ guideOpen: !p.guideOpen }))} style={{ border: 0, background: 'transparent', padding: 0, cursor: 'pointer', animation: 'bob 3.4s ease-in-out infinite', filter: 'drop-shadow(0 0 18px rgba(47,107,255,.55))' }} title="Scout demo guide">
            <Scout agent="scout" style={{ width: 76, height: 78 }} />
          </button>
          {/* No script bubble on Warehouse: it isn't part of the scripted story and would float over the branch grid. */}
          {!s.guideOpen && s.view !== 'warehouse' && (
            <button onClick={() => actions.patch((p) => ({ guideOpen: !p.guideOpen }))} style={{ marginBottom: 26, border: '1px solid #26262E', background: '#0A0A0D', color: '#F2F2F4', fontSize: 12, padding: '7px 11px', borderRadius: '12px 12px 12px 3px', cursor: 'pointer', maxWidth: 260, textAlign: 'left' }}>{shell.guideBubble}</button>
          )}
        </div>
      </div>
    </div>
  );
}
